import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Images, Upload, Maximize, Eraser, Expand, Users, PaintBucket, Settings
} from 'lucide-react';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  section?: string;
}

const navItems: NavItem[] = [
  { label: 'Home', path: '/', icon: <Home size={18} />, section: 'main' },
  { label: 'Library', path: '/library', icon: <Images size={18} />, section: 'main' },
  { label: 'Upscale', path: '/upscale', icon: <Maximize size={18} />, section: 'tools' },
  { label: 'Remove', path: '/remove', icon: <Eraser size={18} />, section: 'tools' },
  { label: 'Extend', path: '/extend', icon: <Expand size={18} />, section: 'tools' },
  { label: 'Variations', path: '/variations', icon: <Users size={18} />, section: 'tools' },
  { label: 'Background', path: '/background', icon: <PaintBucket size={18} />, section: 'tools' },
];

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();

  const mainItems = navItems.filter(i => i.section === 'main');
  const toolItems = navItems.filter(i => i.section === 'tools');

  return (
    <div className="flex flex-col gap-1 pt-2">
      {/* Upload button */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg mb-2 transition-colors text-sm font-medium"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--sage-primary) 15%, transparent)',
          color: 'var(--sage-primary)',
          border: '1px solid color-mix(in srgb, var(--sage-primary) 25%, transparent)',
        }}
      >
        <Upload size={18} />
        Upload Image
      </button>

      {/* Main nav */}
      {mainItems.map(item => (
        <NavButton key={item.path} item={item} active={location.pathname === item.path} onClick={() => navigate(item.path)} />
      ))}

      {/* Tools section */}
      <div
        className="text-[10px] uppercase tracking-[0.15em] px-3 pt-4 pb-1"
        style={{ color: 'var(--sage-text-muted)' }}
      >
        AI Tools
      </div>
      {toolItems.map(item => (
        <NavButton key={item.path} item={item} active={location.pathname === item.path} onClick={() => navigate(item.path)} />
      ))}
    </div>
  );
}

function NavButton({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm w-full text-left"
      style={{
        backgroundColor: active ? 'var(--sage-surface)' : 'transparent',
        color: active ? 'var(--sage-text)' : 'var(--sage-text-muted)',
      }}
      onMouseEnter={e => {
        if (!active) (e.currentTarget.style.backgroundColor = 'var(--sage-white-5)');
      }}
      onMouseLeave={e => {
        if (!active) (e.currentTarget.style.backgroundColor = 'transparent');
      }}
    >
      {item.icon}
      {item.label}
    </button>
  );
}
